"use client";

import type { VoicesSliderBlock } from "@mw/shared/payload-types";

import clsx from "clsx";
import { useTranslations } from "next-intl";

import { HairlineCardBorder } from "@ui/components/core/HairlineCardBorder";
import { Link } from "@ui/components/core/Link";
import ArrowUpRight16 from "@ui/icons/16x16/arrow-up-right-16.svg";

export type VoiceCardVariant = VoicesSliderBlock["cardLinkType"]["template"];

export interface VoiceCardProps {
  /** Accessible label for the voice */
  ariaLabel?: string;
  /** Link URL for the CTA button */
  ctaHref?: string;
  /** Description text */
  description?: string;
  /** Custom media/orb component to render in the center */
  media?: React.ReactNode;
  /** Callback when CTA button is clicked */
  onCtaClick?: () => void;
  /** Title text */
  title: string;
  /** Visual variant of the card */
  variant?: VoiceCardVariant;
  /** Override default CTA button label */
  ctaLabel?: string;
}

export function VoiceCard({
  title,
  description,
  ctaHref,
  ariaLabel,
  media,
  onCtaClick,
  variant = "use-voice",
  ctaLabel,
}: VoiceCardProps) {
  const t = useTranslations("ClientKeys.VoicesSlider");

  const isExploreVariant = variant === "explore-all-voices";
  const isCustomLinkVariant = variant === "custom-link";

  return (
    <article
      aria-label={ariaLabel || title}
      className='tw-group tw-relative tw-isolate tw-w-full tw-aspect-[3/4] tw-rounded-2xl tw-overflow-hidden bg-dark-new'
    >
      {/* CTA button in top right corner - appears on hover (use-voice variant) */}
      {variant === "use-voice" && (
        <div className='tw-absolute tw-top-2 tw-right-2 tw-z-10 tw-opacity-0 tw-transition-opacity tw-duration-300 group-hover:tw-opacity-100 group-focus-within:tw-opacity-100'>
          {ctaHref && (
            <Link asButton buttonVariants={{ variant: "elevated", size: "md" }} href={ctaHref} onClick={onCtaClick}>
              {ctaLabel ?? t("use_voice")}
            </Link>
          )}
        </div>
      )}

      {/* Arrow icon button in top right corner - appears on hover (custom-link variant) */}
      {isCustomLinkVariant && ctaHref && (
        <div className='tw-absolute tw-top-3 tw-right-3 tw-z-10 tw-opacity-0 tw-transition-opacity tw-duration-300 group-hover:tw-opacity-100 group-focus-within:tw-opacity-100'>
          <Link
            asButton
            buttonVariants={{ variant: "elevated", size: "pill" }}
            href={ctaHref}
            onClick={onCtaClick}
            aria-label={title}
          >
            <ArrowUpRight16 />
          </Link>
        </div>
      )}

      {/* Content wrapper - positions media and text together */}
      <div
        className={clsx(
          "tw-absolute tw-inset-0 tw-flex tw-flex-col tw-items-center",
          isCustomLinkVariant ? "tw-justify-center tw-pb-16" : "tw-pt-20",
          isExploreVariant ? "tw-gap-3" : !isCustomLinkVariant && "tw-gap-8",
        )}
      >
        {/* Media */}
        <div className='tw-w-1/2 tw-aspect-square'>{media}</div>

        {/* Text content - positioned at bottom for custom-link variant */}
        <div
          className={clsx(
            "tw-flex tw-flex-col tw-items-center tw-gap-1 tw-px-7 tw-text-center",
            isCustomLinkVariant && "tw-absolute tw-bottom-10 tw-left-0 tw-right-0",
          )}
        >
          <p className='f-heading-05'>{title}</p>
          {description && <p className='tw-opacity-60 tw-line-clamp-2 f-subhead-05'>{description}</p>}
        </div>
      </div>

      {/* Bottom CTA button (exploreVoicesButton variant only) - appears on hover */}
      {isExploreVariant && ctaHref && (
        <div className='tw-absolute tw-bottom-6 tw-left-0 tw-right-0 tw-flex tw-justify-center tw-px-7 tw-opacity-0 tw-transition-opacity tw-duration-300 group-hover:tw-opacity-100 group-focus-within:tw-opacity-100'>
          <Link asButton buttonVariants={{ variant: "elevated", size: "md" }} href={ctaHref} onClick={onCtaClick}>
            {t("explore_all_voices")}
          </Link>
        </div>
      )}

      <HairlineCardBorder />
    </article>
  );
}
